import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
// import { AccountListInterFace } from '../../../interface/account-list-penalty-interface';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import ConvertDDMMYYYYtoISO from '../../../shared-functions/ConvertDDMMYYYYtoISO';
import { stringValidation } from '../../../shared-functions/Validation';
import penaltyManagementApi from '../../../utils/api/penalty-management';
import StationApi from '../../../utils/api/station';

import AuditLog from './AuditLog';

interface OverrideInterface {
  accountCustomId: string;
  stationId: string[];
  ceilingType: string;
  ceilingValue: number;
  triggerMessage: string[];
  effectiveFrom: string;
  effectiveTo: string;
  dynamicPricing: string[];
  stationNames: StationInterface[];
  durationAmountList: DurationAmountListInterface[];
  facilityRequest: boolean;
}

interface OverridePropertiesInterface {
  title: string;
  id?: string | null;
  type: 'edit' | 'add' | 'view';
  closeModal: () => void;
  handleRefresh?: () => void;
}

interface DurationAmountListInterface {
  from: string;
  to: string;
  mode: string;
  amount: string;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

interface StationInterface {
  _id: string;
  name: string;
}

/**
 * Normalize a value to an array
 * @param value - string or string array
 * @returns - array of strings
 */
const normalizeToArray = (value?: string | string[]): string[] => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

/**
 * Edit Penalty
 * @returns
 */
function AddOverrides(properties: OverridePropertiesInterface) {
  const { closeModal, id, type, handleRefresh, title } = properties;
  const { t } = useTranslation('penaltyManagement');

  const dispatch = useAppDispatch();
  const [buttonStatus, setButtonStatus] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ruleErrors, setRuleErrors] = useState<{
    [index: number]: { from: boolean; to: boolean };
  }>({});
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });
  const [override, setOverride] = useState<OverrideInterface>({
    accountCustomId: '',
    stationId: [],
    ceilingType: '',
    ceilingValue: 0,
    triggerMessage: [],
    effectiveFrom: '',
    effectiveTo: '',
    dynamicPricing: [],
    stationNames: [],
    durationAmountList: [{ from: '', to: '', mode: '', amount: '' }],
    facilityRequest: false,
  });

  /**
   * Normal Input Handler
   */
  const handleInputChange = (name: string, value: any) => {
    setErrorMessage(false);
    setOverride((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const allModes = [
    { label: t('grace'), value: 'grace' },
    { label: t('flatRate'), value: 'flatRate' },
    { label: t('perMinute'), value: 'perMinute' },
  ];

  /**
   * Add rule row
   */
  const addRule = () => {
    const index = override.durationAmountList.length - 1;
    const lastRule = override.durationAmountList[index];
    const from = Number(lastRule.from);
    const to = Number(lastRule.to);
    let fromError = false;
    let toError = false;

    // Case 1: Empty values
    if (lastRule.from === '') {
      fromError = true;
    }

    if (lastRule.to === '') {
      toError = true;
    }
    if (lastRule.to === '' || to <= from) {
      toError = true;
    }
    // Case 2: Range invalid
    if (lastRule.from !== '' && lastRule.to !== '' && to < from) {
      fromError = true;
      toError = true;
    }
    // If any error → show error only for this row
    if (fromError || toError) {
      setRuleErrors((previous) => ({
        ...previous,
        [index]: { from: fromError, to: toError },
      }));
      return;
    }

    // Clear error if valid
    setRuleErrors((previous) => ({
      ...previous,
      [index]: { from: false, to: false },
    }));

    // If valid → create new rule
    setOverride((previous) => ({
      ...previous,
      durationAmountList: [
        ...previous.durationAmountList,
        {
          from: String(to), // next rule starts from previous TO
          to: '',
          mode: '',
          amount: '',
        },
      ],
    }));
  };

  /**
   * Delete rule row
   */
  const deleteRule = (index: number) => {
    setOverride((previous) => ({
      ...previous,
      durationAmountList: previous.durationAmountList.filter((_, input) => input !== index),
    }));
  };

  /**
   * Update rule row field value
   */
  const updateRule = (index: number, name: keyof DurationAmountListInterface, value: string) => {
    const rules = [...override.durationAmountList];

    rules[index][name] = value;
    // ✅ If mode is GRACE → force amount = "0"
    if (name === 'mode' && value === 'grace') {
      rules[index].amount = '0';
    }

    // ✅ If switching away from grace → clear amount
    if (name === 'mode' && value !== 'grace') {
      rules[index].amount = '';
    }
    setOverride((previous) => ({
      ...previous,
      durationAmountList: rules,
    }));
  };

  /**
   * Filter available mode options based on already selected modes
   */
  const getAvailableModes = (currentIndex: number) => {
    const selectedModes = override.durationAmountList.map((r) => r.mode).filter(Boolean);

    return allModes.filter((m) => {
      // Allow any mode if it's already selected for the current index (editing case)
      if (override.durationAmountList[currentIndex].mode === m.value) return true;
      // Make "Grace" unique: if already selected anywhere else, hide it
      if (m.value === 'grace' && selectedModes.includes('grace')) {
        return false;
      }

      // Allow Flat Rate & Per Minute multiple times
      return true;
    });
  };

  /**
   * Close modal function
   */
  const handleClose = () => {
    closeModal();
    handleRefresh?.();
    setModalBoxStatus('close');
  };

  /**
   * Rules Valid
   */
  const isRulesValid = (): boolean => {
    let valid = true;
    const errors: {
      [index: number]: { from: boolean; to: boolean };
    } = {};

    override.durationAmountList.forEach((rule, index) => {
      const from = Number(rule.from);
      const to = Number(rule.to);

      let fromError = false;
      let toError = false;

      // 1️ Empty checks
      if (rule.from === '') fromError = true;
      if (rule.to === '') toError = true;

      // 2️ Same value (10–10)
      if (rule.from !== '' && rule.to !== '' && from === to) {
        toError = true;
      }

      // 3️ to < from
      if (rule.from !== '' && rule.to !== '' && to < from) {
        fromError = true;
        toError = true;
      }

      if (index > 0) {
        const previousTo = Number(override.durationAmountList[index - 1].to);

        if (rule.from !== '' && !Number.isNaN(previousTo) && from !== previousTo) {
          fromError = true; // ❌ breaks chain
        }
      }

      if (fromError || toError) {
        valid = false;
        errors[index] = { from: fromError, to: toError };
      }

      // 5️⃣ Mode validation
      if (!rule.mode) valid = false;

      // 6️⃣ Amount validation
      if (rule.mode !== 'grace' && rule.amount === '') valid = false;
    });

    setRuleErrors(errors);
    return valid;
  };
  const isDateInvalid =
    override.effectiveFrom &&
    override.effectiveTo &&
    new Date(ConvertDDMMYYYYtoISO(override.effectiveFrom)) >
      new Date(ConvertDDMMYYYYtoISO(override.effectiveTo));

  /**
   * Submit payload to API
   */
  const handleSubmit = async () => {
    setButtonStatus(true);

    const isFormInvalid =
      override.ceilingType === '' ||
      !stringValidation(1, 100, override.ceilingType) ||
      override.ceilingValue === 0 ||
      override.dynamicPricing.length === 0 ||
      override.triggerMessage.length === 0 ||
      override.effectiveFrom === '' ||
      override.effectiveTo === '' ||
      new Date(ConvertDDMMYYYYtoISO(override.effectiveFrom)) >
        new Date(ConvertDDMMYYYYtoISO(override.effectiveTo)) ||
      !isRulesValid();

    if (isFormInvalid) {
      setErrorMessage(true);
      setButtonStatus(false);
      return;
    }

    if (type === 'add') {
      try {
        const payload = {
          // accountId: override.accountId,
          stationId: override.stationId,
          dynamicPricing: override.dynamicPricing,
          ceilingType: override.ceilingType,
          ceilingValue: override.ceilingValue,
          triggerMessage: override.triggerMessage,
          effectiveFrom: ConvertDDMMYYYYtoISO(override.effectiveFrom),
          effectiveTo: ConvertDDMMYYYYtoISO(override.effectiveTo),
          durationAmountList: override.durationAmountList,
        };
        await penaltyManagementApi.addOverride(payload);
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          rightCorner: '',
          content: (
            <SuccessNotification
              title={t(type === 'add' ? 'overrideAddedSuccess' : 'overrideUpdatedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => handleClose()}
            />
          ),
          notificationModal: true,
        });
      } catch (error: any) {
        setButtonStatus(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      }
    } else {
      try {
        const stationId = type === 'edit' ? override.stationId?.[0] : override.stationId;

        const payload = {
          // accountId: override.accountId,
          stationId,
          dynamicPricing: override.dynamicPricing,
          ceilingType: override.ceilingType,
          ceilingValue: override.ceilingValue,
          triggerMessage: override.triggerMessage,
          effectiveFrom: ConvertDDMMYYYYtoISO(override.effectiveFrom),
          effectiveTo: ConvertDDMMYYYYtoISO(override.effectiveTo),
          durationAmountList: override.durationAmountList,
        };
        await penaltyManagementApi.updateSingleAccountDetails(id as string, payload);
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          rightCorner: '',
          content: (
            <SuccessNotification
              title={t('overrideUpdatedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleClose}
            />
          ),
          notificationModal: true,
        });
      } catch (error: any) {
        setButtonStatus(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      } finally {
        setButtonStatus(false);
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);
    if (!id) {
      setIsLoading(false);
      return;
    }
    penaltyManagementApi.viewSingleAccountDetails(id).then((response) => {
      const data = response.data || {};
      setOverride({
        accountCustomId: data.accountCustomId ?? '',
        stationId: normalizeToArray(data.stationId),
        ceilingType: data.ceilingType ?? '',
        ceilingValue: data.ceilingValue ?? 0,
        triggerMessage: normalizeToArray(data.triggerMessage),
        dynamicPricing: normalizeToArray(data.dynamicPricing),
        stationNames: Array.isArray(data.stationNames) ? data.stationNames : [],
        effectiveFrom: data.effectiveFrom ?? '',
        effectiveTo: data.effectiveTo ?? '',
        durationAmountList: Array.isArray(data.durationAmountList)
          ? data.durationAmountList.map((item: any) => ({
              from: String(item?.from ?? ''),
              to: String(item?.to ?? ''),
              mode: String(item?.mode ?? ''),
              amount: String(item?.amount ?? ''),
            }))
          : [{ from: '', to: '', mode: '', amount: '' }],

        facilityRequest: Boolean(data.facilityRequest),
      });
      setIsLoading(false);
    });
  }, [id]);

  /**
   * handle changes for the station API
   */
  const handleStationList = async () => {
    try {
      const response = await StationApi.fetchAllStations();
      setOverride((previous) => ({
        ...previous,
        stationNames: Array.isArray(response?.data) ? response.data : [],
      }));
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 404,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    handleStationList();
  }, []);

  return (
    <div className={`override-wrap ${type === 'add' ? 'add' : ''}`}>
      {isLoading ? (
        <div className="loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <ModalBox
            open={modalBoxStatus === 'open'}
            title={modalBoxDetail.title}
            content={modalBoxDetail.content}
            closeModal={() => setModalBoxStatus('close')}
            // rightCorner={modalBoxDetail.rightCorner}
            notification={modalBoxDetail.notificationModal}
            header={!modalBoxDetail.notificationModal}
          />
          <div>
            {' '}
            <PageTitle title={title} buttonType="none" />
          </div>{' '}
          <div className={`override-container ${type === 'edit' || type === 'view' ? 'edit' : ''}`}>
            <div className="override-left">
              {type === 'edit' && (
                <div className="switch-button">
                  <SwitchButton
                    checked={override.facilityRequest}
                    change={(checked) => handleInputChange('facilityRequest', checked)}
                  />
                  <span>{t('penaltyFeature')}</span>
                </div>
              )}
              <div className="inputs-container">
                {/* <CustomInput
                  name="accountId"
                  inputLabel="Account Id"
                  type="text"
                  placeholder="Enter Value"
                  value={override.accountId}
                  onChangeValue={handleInputChange}
                  // error={errorMessage && override.accountId === ''}
                  // errorMessage="Required"
                  disabled={type === 'edit'}
                /> */}
                <div className="input-side-by-side">
                  <MultiSelectDropdown
                    name="stationId"
                    title={t('stationId')}
                    selectedValues={override.stationId}
                    options={(override.stationNames ?? []).map((station) => ({
                      label: station.name,
                      value: station._id,
                    }))}
                    valueRequired
                    onSelectionChange={handleInputChange}
                    placeholder={t('selectStation')}
                    placeholderFilled={t('stations')}
                    error={errorMessage && override.stationId.length === 0}
                    errorMessage={t('required')}
                    disabled={type === 'edit'}
                  />
                  <MultiSelectDropdown
                    name="dynamicPricing"
                    title={t('dynamicPricing')}
                    selectedValues={override.dynamicPricing}
                    onSelectionChange={handleInputChange}
                    placeholder={t('selectOption')}
                    placeholderFilled={t('options')}
                    options={[
                      { label: t('base'), value: 'base' },
                      { label: t('peak'), value: 'peak' },
                      { label: t('offPeak'), value: 'offpeak' },
                    ]}
                    search={false}
                    multiSelectDisable={false}
                    valueRequired
                    fontSize="small"
                    error={errorMessage && override.dynamicPricing.length === 0}
                    errorMessage={t('required')}
                  />
                  <SelectFiled
                    name="ceilingType"
                    inputLabel={t('ceilingType')}
                    selectedValue={override.ceilingType}
                    placeholder={t('selectCeilingType')}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={errorMessage && override.ceilingType === ''}
                    errorMessage={t('required')}
                    options={[
                      { label: t('fixedAmount'), value: 'fixedAmount' },
                      { label: t('percentageOfBase'), value: 'percentage' },
                    ]}
                  />
                  <CustomInput
                    name="ceilingValue"
                    inputLabel={t('maxCeilingValue')}
                    type="number"
                    placeholder={t('enterValue')}
                    value={override.ceilingValue}
                    onChangeValue={handleInputChange}
                    error={errorMessage && override.ceilingValue <= 0}
                    errorMessage={t('required')}
                    valueRequired
                    disallowNegative
                  />
                </div>
                <MultiSelectDropdown
                  name="triggerMessage"
                  title={t('triggerMessage')}
                  selectedValues={override.triggerMessage}
                  onSelectionChange={handleInputChange}
                  placeholder={t('selectOption')}
                  placeholderFilled={t('options')}
                  options={[
                    { label: t('suspendedEvse'), value: 'SuspendedEVSE' },
                    { label: t('suspendedEv'), value: 'SuspendedEV' },
                    { label: t('faulted'), value: 'Faulted' },
                    { label: t('finishing'), value: 'Finishing' },
                  ]}
                  search={false}
                  multiSelectDisable={false}
                  valueRequired
                  fontSize="small"
                  selectedChips
                  error={errorMessage && override.triggerMessage.length === 0}
                  errorMessage={t('required')}
                />
                <div className="date-box-content">
                  <CustomDatePicker
                    inputLabel={t('startDate')}
                    value={override.effectiveFrom}
                    name="effectiveFrom"
                    onChangeValue={handleInputChange}
                    placeholder={t('dateFormat')}
                    valueRequired
                    disablePastDates
                    error={Boolean(
                      errorMessage && (override.effectiveFrom === '' || isDateInvalid),
                    )}
                    errorMessage={
                      override.effectiveFrom === ''
                        ? t('startDateRequired')
                        : t('startDateGreaterError')
                    }
                  />
                  <CustomDatePicker
                    inputLabel={t('endDate')}
                    value={override.effectiveTo}
                    name="effectiveTo"
                    onChangeValue={handleInputChange}
                    placeholder={t('dateFormat')}
                    error={Boolean(errorMessage && (override.effectiveTo === '' || isDateInvalid))}
                    errorMessage={
                      override.effectiveTo === '' ? t('endDateRequired') : t('endDateLessError')
                    }
                    disablePastDates
                    valueRequired
                  />
                </div>
              </div>
              <div className="pricing-wrap">
                {override.durationAmountList.map((rule, index) => (
                  <div className="rule-box-container">
                    <div className="rule-box" key={rule.from}>
                      <CustomInput
                        name="from"
                        inputLabel={t('fromMins')}
                        placeholder="0"
                        type="number"
                        value={rule.from}
                        onChangeValue={(name, value) =>
                          updateRule(
                            index,
                            name as keyof DurationAmountListInterface,
                            String(value),
                          )
                        }
                        error={
                          ruleErrors[index]?.from &&
                          errorMessage &&
                          override.durationAmountList[index].from === ''
                        }
                        errorMessage={t('invalidRange')}
                        disallowNegative
                      />{' '}
                      <CustomInput
                        name="to"
                        inputLabel={t('toMins')}
                        placeholder="0"
                        type="number"
                        value={rule.to}
                        onChangeValue={(name, value) =>
                          updateRule(
                            index,
                            name as keyof DurationAmountListInterface,
                            String(value),
                          )
                        }
                        error={
                          ruleErrors[index]?.to &&
                          errorMessage &&
                          override.durationAmountList[index].to === ''
                        }
                        errorMessage={t('invalidRange')}
                        disallowNegative
                      />
                      <SelectFiled
                        name="mode"
                        inputLabel={t('mode')}
                        placeholder={t('selectMode')}
                        selectedValue={rule.mode}
                        onChangeValue={(name, value) =>
                          updateRule(
                            index,
                            name as keyof DurationAmountListInterface,
                            String(value),
                          )
                        }
                        options={getAvailableModes(index)}
                        error={rule.mode === '' && errorMessage}
                        errorMessage={t('modeRequired')}
                      />
                      <CustomInput
                        name="amount"
                        inputLabel={t('amount')}
                        placeholder={t('enterValue')}
                        type="number"
                        value={rule.amount}
                        onChangeValue={(name, value) =>
                          updateRule(
                            index,
                            name as keyof DurationAmountListInterface,
                            String(value),
                          )
                        }
                        disabled={rule.mode === 'grace'} // ✅ disable for grace
                        error={rule.amount === '' && errorMessage}
                        errorMessage={t('amountRequired')}
                        disallowNegative
                      />
                      <div className="delete-button">
                        {override.durationAmountList.length > 1 && (
                          <MyButton
                            label=""
                            svgIcon={<DeleteIcon />}
                            buttonType="error"
                            onClickFunc={() => deleteRule(index)}
                            disableButton={buttonStatus}
                          />
                        )}
                      </div>
                    </div>
                    <div className="add-button">
                      {index === override.durationAmountList.length - 1 && (
                        <MyButton
                          label={t('add')}
                          svgIcon={<PlusIcon />}
                          buttonType="primary-outline-none"
                          onClickFunc={addRule}
                          disableButton={buttonStatus}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="button-row">
                <MyButton
                  label={t('cancel')}
                  buttonType="primary-outline"
                  onClickFunc={closeModal}
                  disableButton={buttonStatus}
                />
                <MyButton
                  label={t('submit')}
                  buttonType="submit"
                  onClickFunc={handleSubmit}
                  loading={buttonStatus}
                />
              </div>
            </div>
            {type !== 'add' && (
              <div className="AuditLog">
                {id && id === 'okay' && (
                  <AuditLog
                    currentData={{
                      id,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

AddOverrides.defaultProps = {
  id: null,
};
export default AddOverrides;
